import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchAuthSession } from 'aws-amplify/auth';
import { ParentBottomTabBar } from '@/components/ui/ParentBottomTabBar';
import { useAuthStore } from '@/store/authStore';
import { GlobalHeader } from '@/components/GlobalHeader';
import { apiFetch } from '@/services/api';
import { LanguageSwitcher } from '@/components/ui/LanguageSwitcher';

export function ParentMorePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const displayName = useAuthStore((s) => s.displayName);
  const signOut = useAuthStore((s) => s.signOut);

  const [email, setEmail] = useState<string | null>(null);
  const [notifEnabled, setNotifEnabled] = useState(true);
  const notifSaving = useRef(false);

  useEffect(() => {
    fetchAuthSession()
      .then((session) => {
        const payload = session.tokens?.idToken?.payload as Record<string, unknown> | undefined;
        setEmail((payload?.['email'] as string | undefined) ?? null);
      })
      .catch(() => undefined);
  }, []);

  const toggleNotifications = async () => {
    if (notifSaving.current) return;
    notifSaving.current = true;
    const next = !notifEnabled;
    setNotifEnabled(next);
    try {
      await apiFetch('/notifications/preferences', {
        method: 'POST',
        body: JSON.stringify({ enabled: next }),
      });
    } catch {
      setNotifEnabled(!next); // revert on error
    } finally {
      notifSaving.current = false;
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  return (
    <div className="page-center">
      <div className="phone-mockup">
        <GlobalHeader />
        <div className="app-content" style={{ paddingBottom: '24px' }}>

          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 24px' }}>
            Settings & Profile
          </h1>

          {/* Profile card */}
          <div style={{
            background: 'white', padding: '20px', borderRadius: '12px',
            marginBottom: '24px', border: '1px solid var(--border)',
          }}>
            <p style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 4px' }}>
              {displayName ?? t('parent_student.role_parent')}
            </p>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: '0 0 8px' }}>
              {t('parent_student.role_parent')}
            </p>
            {email && (
              <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', margin: '0 0 16px' }}>
                {email}
              </p>
            )}

            {/* Application language */}
            <div style={{ paddingTop: '16px', borderTop: '1px solid var(--border)' }}>
              <LanguageSwitcher label={t('settings.app_language')} />
            </div>
          </div>

          {/* Notification preference */}
          <div style={{
            background: 'white', padding: '16px 20px', borderRadius: '12px',
            marginBottom: '24px', border: '1px solid var(--border)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div>
              <p style={{ margin: 0, fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-dark)' }}>
                {t('notifications.study_reminders')}
              </p>
              <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                {t('notifications.daily_digest_desc')}
              </p>
            </div>
            <button
              type="button"
              onClick={toggleNotifications}
              aria-label={notifEnabled ? t('student_more.disable_notifications') : t('student_more.enable_notifications')}
              style={{
                width: '46px', height: '26px', borderRadius: '13px', border: 'none',
                background: notifEnabled ? 'var(--primary, #4F46E5)' : '#D1D5DB',
                position: 'relative', cursor: 'pointer', transition: 'background 0.2s',
                flexShrink: 0,
              }}
            >
              <span style={{
                position: 'absolute', top: '3px',
                left: notifEnabled ? '23px' : '3px',
                width: '20px', height: '20px', borderRadius: '50%',
                background: 'white', transition: 'left 0.2s',
              }} />
            </button>
          </div>

          {/* General Info */}
          <div style={{
            background: 'white', borderRadius: '12px',
            marginBottom: '24px', border: '1px solid var(--border)',
            overflow: 'hidden',
          }}>
            <p style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', padding: '12px 20px 8px' }}>
              {t('general_info.title')}
            </p>
            <a
              href={`${import.meta.env.VITE_WEBSITE_URL ?? 'https://vadostudy.com'}?view=guide&lang=${i18n.resolvedLanguage}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', borderTop: '1px solid var(--border)', color: 'var(--text-dark)', textDecoration: 'none', fontSize: '0.9rem' }}
            >
              <span>📖 {t('general_info.user_guide')}</span>
              <span style={{ color: 'var(--text-muted)' }}>↗</span>
            </a>
            <a
              href={import.meta.env.VITE_WEBSITE_URL ?? 'https://vadostudy.com'}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', borderTop: '1px solid var(--border)', color: 'var(--text-dark)', textDecoration: 'none', fontSize: '0.9rem' }}
            >
              <span>🌐 {t('general_info.website')}</span>
              <span style={{ color: 'var(--text-muted)' }}>↗</span>
            </a>
          </div>

          {/* Sign out */}
          <button
            type="button"
            onClick={handleSignOut}
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: 'var(--danger)', fontWeight: 600, fontSize: '1rem',
              padding: '16px 0', textAlign: 'left', width: '100%',
            }}
          >
            🚪 {t('actions.signout')}
          </button>

        </div>

        <ParentBottomTabBar activeTab="More" />
      </div>
    </div>
  );
}
